import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'

function Terms() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const sections = [
    {
      title: '1. Acceptance of Terms',
      content: [
        {
          text: 'By installing, accessing, or using HireFlow ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service. We reserve the right to modify these terms at any time, and your continued use constitutes acceptance of any changes.',
        },
      ],
    },
    {
      title: '2. Description of Service',
      content: [
        {
          text: 'HireFlow is a Chrome extension that provides AI-powered resume generation and career tools. The Service includes:',
        },
        {
          text: '• Converting LinkedIn profiles into professional resumes',
        },
        {
          text: '• Generating job-matched CVs tailored to specific positions',
        },
        {
          text: '• ATS (Applicant Tracking System) CV scoring and optimization',
        },
        {
          text: '• LinkedIn profile scoring and improvement suggestions',
        },
        {
          text: '• AI-powered cover letter generation',
        },
        {
          text: '• LinkedIn profile generation from resume data',
        },
        {
          text: '• Access to professional resume templates',
        },
      ],
    },
    {
      title: '3. User Accounts and Eligibility',
      content: [
        {
          subtitle: '3.1 Account Creation',
          text: 'To access certain features, you must create an account. You agree to provide accurate, current information and maintain the security of your account credentials.',
        },
        {
          subtitle: '3.2 Eligibility',
          text: 'You must be at least 16 years old to use HireFlow. By using the Service, you represent that you meet this requirement and have the legal capacity to enter into these terms.',
        },
        {
          subtitle: '3.3 Account Security',
          text: 'You are responsible for all activity under your account. Notify us immediately of any unauthorized access or security breaches.',
        },
      ],
    },
    {
      title: '4. Subscription Plans and Payments',
      content: [
        {
          subtitle: '4.1 Free Tier',
          text: 'HireFlow offers a free tier with limited features. We reserve the right to modify free tier limitations at any time with reasonable notice.',
        },
        {
          subtitle: '4.2 Paid Subscriptions',
          text: 'Paid plans (Starter, Professional, and Lifetime) are billed according to the pricing page. Subscriptions automatically renew unless cancelled before the renewal date.',
        },
        {
          subtitle: '4.3 Payment Processing',
          text: 'All payments are processed securely through Paddle. By subscribing, you authorize us to charge your payment method for the selected plan and any applicable taxes.',
        },
        {
          subtitle: '4.4 Price Changes',
          text: 'We may change subscription prices with 30 days\' notice. Changes apply to new subscriptions and renewals after the notice period.',
        },
        {
          subtitle: '4.5 Cancellation',
          text: 'You may cancel your subscription at any time. Access continues until the end of your current billing period. No refunds for partial periods unless covered by our refund policy.',
        },
      ],
    },
    {
      title: '5. Usage Limits and Fair Use',
      content: [
        {
          subtitle: '5.1 Usage Quotas',
          text: 'Each plan has specific usage limits (e.g., number of resumes per month). Exceeding limits may require an upgrade or result in temporary service restrictions.',
        },
        {
          subtitle: '5.2 Fair Use Policy',
          text: 'The Service is for personal, non-commercial use unless you have a business agreement with us. Automated or bulk usage without authorization is prohibited.',
        },
        {
          subtitle: '5.3 Prohibited Uses',
          text: 'You may not: (a) use the Service for illegal purposes, (b) attempt to reverse engineer or exploit the Service, (c) share your account with others, (d) use automated scripts or bots, (e) violate third-party rights (including LinkedIn\'s terms), or (f) resell or redistribute Service features.',
        },
      ],
    },
    {
      title: '6. Intellectual Property Rights',
      content: [
        {
          subtitle: '6.1 Service Ownership',
          text: 'HireFlow, including all software, algorithms, templates, and content, is owned by us and protected by copyright, trademark, and other intellectual property laws.',
        },
        {
          subtitle: '6.2 Your Content',
          text: 'You retain ownership of your LinkedIn data and generated resumes. By using the Service, you grant us a limited license to process your data solely to provide the Service.',
        },
        {
          subtitle: '6.3 Generated Content',
          text: 'Resumes and other documents created using HireFlow are yours to use. However, the underlying templates and AI technology remain our property.',
        },
        {
          subtitle: '6.4 Feedback',
          text: 'Any feedback, suggestions, or ideas you provide may be used by us without compensation or attribution.',
        },
      ],
    },
    {
      title: '7. Data Privacy and LinkedIn Integration',
      content: [
        {
          subtitle: '7.1 Data Collection',
          text: 'Our use of your data is governed by our Privacy Policy. By using HireFlow, you consent to data collection and processing as described.',
        },
        {
          subtitle: '7.2 LinkedIn Terms Compliance',
          text: 'HireFlow accesses LinkedIn data in accordance with LinkedIn\'s Terms of Service and API guidelines. You are responsible for ensuring your LinkedIn account complies with LinkedIn\'s terms.',
        },
        {
          subtitle: '7.3 Data Security',
          text: 'While we implement industry-standard security measures, no system is 100% secure. You use the Service at your own risk.',
        },
      ],
    },
    {
      title: '8. Service Availability and Modifications',
      content: [
        {
          subtitle: '8.1 Service Availability',
          text: 'We strive for 99.9% uptime but do not guarantee uninterrupted access. The Service may be temporarily unavailable for maintenance, updates, or due to factors beyond our control.',
        },
        {
          subtitle: '8.2 Changes to Service',
          text: 'We may modify, suspend, or discontinue any feature at any time without liability. We will provide reasonable notice for significant changes affecting paid subscribers.',
        },
        {
          subtitle: '8.3 Third-Party Dependencies',
          text: 'HireFlow relies on third-party services (LinkedIn, OpenAI, etc.). Changes to these services may affect functionality.',
        },
      ],
    },
    {
      title: '9. Disclaimers and Limitations of Liability',
      content: [
        {
          subtitle: '9.1 No Warranty',
          text: 'THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT USING HIREFLOW WILL RESULT IN JOB OFFERS OR INTERVIEWS.',
        },
        {
          subtitle: '9.2 Limitation of Liability',
          text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY IS LIMITED TO THE AMOUNT YOU PAID IN THE PAST 12 MONTHS.',
        },
        {
          subtitle: '9.3 AI-Generated Content',
          text: 'AI-generated resumes and content are suggestions only. You are responsible for reviewing, editing, and ensuring accuracy of all generated content before use.',
        },
        {
          subtitle: '9.4 User Responsibility',
          text: 'You are solely responsible for the content of your resumes and how you use them. We are not responsible for any consequences of resume submissions.',
        },
      ],
    },
    {
      title: '10. Indemnification',
      content: [
        {
          text: 'You agree to indemnify and hold harmless HireFlow and its officers, employees, and agents from any claims, damages, or expenses (including legal fees) arising from your use of the Service, violation of these terms, or infringement of third-party rights.',
        },
      ],
    },
    {
      title: '11. Termination',
      content: [
        {
          subtitle: '11.1 Termination by You',
          text: 'You may terminate your account at any time through account settings or by contacting support.',
        },
        {
          subtitle: '11.2 Termination by Us',
          text: 'We may suspend or terminate your account for violations of these terms, fraudulent activity, or any reason with reasonable notice (except for immediate threats to the Service).',
        },
        {
          subtitle: '11.3 Effect of Termination',
          text: 'Upon termination, your access to the Service ends immediately. Data may be retained as described in our Privacy Policy.',
        },
      ],
    },
    {
      title: '12. Dispute Resolution',
      content: [
        {
          subtitle: '12.1 Governing Law',
          text: 'These terms are governed by the laws of the United States and the State of Delaware, without regard to conflict of law principles.',
        },
        {
          subtitle: '12.2 Informal Resolution',
          text: 'Before filing a claim, you agree to contact us at hireflowapp@gmail.com to attempt informal resolution.',
        },
        {
          subtitle: '12.3 Arbitration',
          text: 'Any disputes will be resolved through binding arbitration in accordance with the American Arbitration Association rules, rather than in court, except where prohibited by law.',
        },
        {
          subtitle: '12.4 Class Action Waiver',
          text: 'You agree to resolve disputes individually and waive any right to participate in class actions or representative proceedings.',
        },
      ],
    },
    {
      title: '13. General Provisions',
      content: [
        {
          subtitle: '13.1 Entire Agreement',
          text: 'These Terms, together with our Privacy Policy and Refund Policy, constitute the entire agreement between you and HireFlow.',
        },
        {
          subtitle: '13.2 Severability',
          text: 'If any provision is found unenforceable, the remaining provisions remain in effect.',
        },
        {
          subtitle: '13.3 No Waiver',
          text: 'Our failure to enforce any right or provision does not constitute a waiver of that right.',
        },
        {
          subtitle: '13.4 Assignment',
          text: 'You may not assign these terms without our consent. We may assign our rights and obligations to any successor or acquirer.',
        },
      ],
    },
    {
      title: '14. Contact Information',
      content: [
        {
          text: 'For questions about these Terms of Service, please contact us:',
        },
        {
          text: 'Email: hireflowapp@gmail.com',
        },
        {
          text: 'Support: hireflowapp@gmail.com',
        },
      ],
    },
  ]

  return (
    <>
      <Helmet>
        <title>Terms of Service - HireFlow</title>
        <meta
          name="description"
          content="Read HireFlow's Terms of Service to understand your rights and responsibilities when using our AI-powered resume builder."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-neutral-50 to-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="heading-xl mb-6">Terms of Service</h1>
            <p className="text-lg text-neutral-600 mb-4">
              <strong>Effective Date:</strong> December 1, 2025
            </p>
            <p className="text-lg text-neutral-600">
              Welcome to HireFlow. These Terms of Service ("Terms") govern your use of our Chrome extension and related services. Please read them carefully before using our Service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-100px' }}
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                    {section.title}
                  </h2>
                  <div className="space-y-4">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        {item.subtitle && (
                          <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                            {item.subtitle}
                          </h3>
                        )}
                        <p className="text-neutral-600 leading-relaxed">
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Important Notice */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mt-12 card p-8 bg-yellow-50 border-yellow-200"
            >
              <h3 className="text-xl font-bold text-neutral-900 mb-4">
                Important Notice
              </h3>
              <p className="text-neutral-700 mb-4">
                By using HireFlow, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree, please discontinue use of the Service immediately.
              </p>
              <p className="text-neutral-700">
                These terms include important provisions such as limitation of liability, dispute resolution through arbitration, and a class action waiver that affect your legal rights. Please review them carefully.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Terms
